/* eslint-disable react/prop-types */
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { XMarkIcon, PlusIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useState, useEffect, forwardRef, useImperativeHandle, useMemo } from 'react';
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import makeAnimated from 'react-select/animated';
import { Fieldset, FieldGroup } from '../../../components/fieldset';
import { Heading } from '../../../components/heading';
import { Text } from '../../../components/text';
import 'react-datepicker/dist/react-datepicker.css';
import { type Step } from './StepNavigation';
import { ProfessionalDetailsFormData, Option } from './types';
import {
  BrandLegend,
  onboardingStepHasVisibleWarnings,
  reactSelectReset,
  StepHeaderStatusBanner,
  ValidationMessage,
} from './utils';

const animatedComponents = makeAnimated();

const StateLicenseSelect = ({ formData, handleChange, stateOptions, validationMessages }) => {
  const handleStateChange = (selected: Option[] | null) => {
    handleChange('licensedStates', selected || []);
  };

  const toggleResident = (e: React.MouseEvent) => {
    e.preventDefault();
    const newIsResident = !formData.isResident;
    handleChange('isResident', newIsResident);

    if (!newIsResident) {
      handleChange('estimatedGraduationDate', null);
    }
  };

  return (
    <div className="tw:space-y-2">
      <Select
        isMulti
        name="licensedStates"
        options={stateOptions}
        value={formData.licensedStates}
        onChange={handleStateChange}
        getOptionLabel={(e) => e.value}
        getOptionValue={(e) => e.value}
        className="tw:w-full"
        styles={reactSelectReset}
        components={animatedComponents}
        hideSelectedOptions={false}
        placeholder={
          formData.isResident
            ? 'What state(s) do you expect to be licensed in?'
            : 'What state(s) are you licensed in?'
        }
      />

      <div className="tw:flex tw:justify-between tw:items-center">
        <div>
          <ValidationMessage
            message={validationMessages.licensedStates?.message}
            icon={validationMessages.licensedStates?.icon}
            colorClass={validationMessages.licensedStates?.colorClass}
          />
        </div>
        <button
          onClick={toggleResident}
          className="tw:text-brand-green-1 tw:underline tw:text-sm tw:block tw:hover:cursor-pointer tw:dark:text-brand-green-1"
        >
          {formData.isResident ? 'Not a resident' : 'I am a resident'}
        </button>
      </div>

      {formData.isResident && (
        <>
          <div className="tw:mt-4">
            <DatePicker
              className="tw:relative tw:block tw:w-full tw:appearance-none tw:rounded-lg tw:px-[calc(theme(spacing[3.5])-1px)] tw:py-[calc(theme(spacing[2.5])-1px)] tw:sm:px-[calc(theme(spacing[3])-1px)] tw:sm:py-[calc(theme(spacing[1.5])-1px)] tw:text-base/6 tw:text-zinc-950 tw:placeholder:text-zinc-500 tw:sm:text-sm/6 tw:dark:text-white tw:border tw:border-zinc-950/10 tw:data-[hover]:border-zinc-950/20 tw:dark:border-white/10 tw:dark:data-[hover]:border-white/20 tw:bg-transparent tw:dark:bg-white/5 tw:focus:outline-none tw:data-[invalid]:border-red-500 tw:data-[invalid]:data-[hover]:border-red-500 tw:data-[invalid]:dark:border-red-500 tw:data-[invalid]:data-[hover]:dark:border-red-500 tw:data-[disabled]:border-zinc-950/20 tw:dark:data-[hover]:data-[disabled]:border-white/15 tw:data-[disabled]:dark:border-white/15 tw:data-[disabled]:dark:bg-white/[2.5%] tw:dark:[color-scheme:dark]"
              wrapperClassName="tw:relative tw:block tw:w-full tw:before:absolute tw:before:inset-px tw:before:rounded-[calc(theme(borderRadius.lg)-1px)] tw:before:bg-white tw:before:shadow tw:dark:before:hidden tw:after:pointer-events-none tw:after:absolute tw:after:inset-0 tw:after:rounded-lg tw:after:ring-inset tw:after:ring-transparent tw:sm:after:focus-within:ring-2 tw:sm:after:focus-within:ring-brand-green-0 tw:has-[[data-disabled]]:opacity-50 tw:before:has-[[data-disabled]]:bg-zinc-950/5 tw:before:has-[[data-disabled]]:shadow-none tw:before:has-[[data-invalid]]:shadow-red-500/10"
              selected={formData.estimatedGraduationDate}
              onChange={(date: Date) => handleChange('estimatedGraduationDate', date)}
              placeholderText="Estimated Graduation Date"
            />
          </div>
          <div>
            <ValidationMessage
              message={validationMessages.estimatedGraduationDate?.message}
              colorClass={validationMessages.estimatedGraduationDate?.colorClass}
            />
          </div>
        </>
      )}
    </div>
  );
};

const BoardCertificationModal = ({
  isOpen,
  onClose,
  onAdd,
  existingCerts,
  boardCertificationOptions,
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredOptions = boardCertificationOptions.filter((option) =>
    option.value.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Dialog open={isOpen} onClose={onClose} className="tw:relative tw:z-50">
      <div className="tw:fixed tw:inset-0 tw:bg-black/30" aria-hidden="true" />
      <div className="tw:fixed tw:inset-0 tw:flex tw:items-start tw:justify-center">
        <DialogPanel className="tw:w-full tw:max-w-md tw:max-h-[85vh] tw:bg-white tw:dark:bg-zinc-600 tw:rounded-lg tw:flex tw:flex-col tw:mt-4">
          <div className="tw:flex-none tw:p-6 tw:border-b tw:border-zinc-200 tw:dark:border-white/10 tw:flex tw:items-center tw:justify-between">
            <DialogTitle className="tw:!text-base tw:font-medium tw:text-zinc-950 tw:dark:!text-white/90 tw:!mb-0">
              Select Board Certification(s) or Eligibility
            </DialogTitle>
            <button
              type="button"
              onClick={onClose}
              className="tw:text-zinc-500 tw:hover:text-zinc-700 tw:dark:text-white/90 tw:dark:hover:text-white"
            >
              <XMarkIcon className="tw:h-5 tw:w-5" />
            </button>
          </div>

          <div className="tw:flex-none tw:p-4 tw:border-b tw:border-zinc-200 tw:dark:border-white/10">
            <input
              type="text"
              placeholder="Search board certifications..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="tw:w-full tw:px-3 tw:py-2 tw:border tw:border-zinc-950/10 tw:rounded-lg tw:focus:outline-none tw:focus:ring-2 tw:focus:ring-brand-green-1 tw:bg-white tw:dark:bg-white/5 tw:dark:border-white/10 tw:dark:text-white/90"
            />
            <p className="tw:text-sm tw:text-zinc-500 tw:dark:text-zinc-400 tw:!mb-0 tw:!mt-3">
              If you are not boarded or eligible, select &quot;NONE.&quot;
            </p>
          </div>

          <div className="tw:flex-1 tw:overflow-y-auto tw:p-6">
            <div className="tw:space-y-2">
              {filteredOptions.map((option) => {
                const isSelected = existingCerts.some((cert) => cert.value === option.value);
                return (
                  <button
                    key={option.value}
                    className={`tw:w-full tw:text-left tw:p-2 tw:rounded tw:transition-colors ${
                      isSelected
                        ? 'tw:bg-brand-green-1 tw:text-white tw:cursor-not-allowed'
                        : 'tw:hover:bg-brand-green-0 tw:active:bg-brand-green-1 tw:text-zinc-950 tw:hover:text-white tw:dark:text-white/90'
                    }`}
                    onClick={() => {
                      if (!isSelected) {
                        onAdd({ certification: option, boardEligible: false });
                      }
                    }}
                    disabled={isSelected}
                  >
                    {option.value}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="tw:flex-none tw:p-6 tw:border-t tw:border-zinc-200 tw:dark:border-white/10">
            <button
              onClick={onClose}
              className="tw:w-full tw:px-4 tw:py-2 tw:bg-white tw:dark:bg-white/5 tw:border tw:border-zinc-950/10 tw:dark:border-white/10 tw:!rounded tw:hover:border-zinc-950/20 tw:dark:hover:border-white/20 tw:text-zinc-950 tw:dark:text-white/90"
            >
              Close
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};

const BoardCertificationList = ({ formData, handleChange, boardCertificationOptions }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const handleAdd = (newCert: { certification: Option; boardEligible: boolean }) => {
    handleChange('boardCertifications', [
      ...formData.boardCertifications,
      {
        value: newCert.certification.value,
        boardEligible: newCert.boardEligible,
      },
    ]);
    setIsModalOpen(false);
  };

  const handleRemove = (certValue: string) => {
    handleChange(
      'boardCertifications',
      formData.boardCertifications.filter((cert) => cert.value !== certValue)
    );
  };

  const handleToggleEligible = (certValue: string) => {
    handleChange(
      'boardCertifications',
      formData.boardCertifications.map((cert) =>
        cert.value === certValue ? { ...cert, boardEligible: !cert.boardEligible } : cert
      )
    );
  };

  return (
    <div className="tw:!space-y-2">
      <button
        type="button"
        onClick={() => setIsModalOpen(true)}
        className="tw:w-full tw:flex tw:items-center tw:gap-2 tw:px-4 tw:py-2 tw:bg-brand-green-1 tw:!rounded-lg tw:hover:bg-brand-green-1/90 tw:active:bg-brand-green-1/90 tw:text-sm tw:transition-colors tw:dark:bg-white/5 tw:dark:text-white tw:dark:border-white/10 tw:dark:hover:bg-white/10 tw:dark:active:bg-white/20"
      >
        <PlusIcon className="tw:h-4 tw:w-4 tw:text-white" />
        <span className="tw:text-white">Add Board Certification...</span>
      </button>

      <div className="tw:space-y-2">
        {formData.boardCertifications.map((cert) => (
          <div
            key={cert.value}
            className="tw:flex tw:items-center tw:gap-2 tw:p-2 tw:bg-brand-green-0/5 tw:border tw:border-brand-green-0 tw:rounded-lg tw:dark:border-brand-green-1 tw:dark:bg-white/5"
          >
            <button
              type="button"
              onClick={() => handleRemove(cert.value)}
              className="tw:text-brand-green-0 tw:hover:text-brand-green-1 tw:dark:text-brand-green-1 tw:dark:hover:text-brand-green-1/90"
            >
              <XMarkIcon className="tw:h-4 tw:w-4" />
            </button>
            <span className="tw:flex-1 tw:text-brand-green-0 tw:dark:text-brand-green-1">
              {boardCertificationOptions.find((opt) => opt.value === cert.value)?.value ||
                cert.value}
            </span>
            {cert.value !== 'NONE' && (
              <label className="tw:!flex tw:items-center tw:gap-2">
                <span className="tw:text-sm tw:dark:text-brand-green-1">Board Eligible?</span>
                <input
                  type="checkbox"
                  checked={cert.boardEligible}
                  onChange={() => handleToggleEligible(cert.value)}
                  className="tw:flex-shrink-0 tw:w-5 tw:h-5 tw:ml-4 tw:appearance-none tw:rounded tw:border-2 tw:border-zinc-300 tw:checked:border-brand-green-1 tw:checked:bg-white tw:relative tw:before:content-[''] tw:before:block tw:before:w-3 tw:before:h-3 tw:before:absolute tw:before:top-1/2 tw:before:left-1/2 tw:before:-translate-x-1/2 tw:before:-translate-y-1/2 tw:checked:before:bg-brand-green-1 tw:transition-all tw:dark:border-white/10 tw:dark:checked:bg-transparent tw:dark:checked:border-brand-green-1 tw:disabled:opacity-90 tw:disabled:cursor-not-allowed"
                />
              </label>
            )}
          </div>
        ))}
      </div>

      <BoardCertificationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onAdd={handleAdd}
        existingCerts={formData.boardCertifications}
        boardCertificationOptions={boardCertificationOptions}
      />
    </div>
  );
};

interface SelectProps {
  formData: ProfessionalDetailsFormData;
  handleChange: (field: string, value: Option[] | null | Date | boolean) => void;
  degreeOptions: Option[];
  specialtyOptions: Option[];
}

const DegreeSelect = ({ formData, handleChange, degreeOptions }: SelectProps) => {
  const handleDegreeChange = (selected: Option[] | null) => {
    handleChange('degree', selected || []);
  };

  return (
    <div className="tw:space-y-2">
      <Select
        isMulti
        name="degree"
        options={degreeOptions}
        value={formData.degree}
        onChange={handleDegreeChange}
        getOptionLabel={(option: Option) => option.label}
        getOptionValue={(option: Option) => option.value}
        className="tw:w-full"
        styles={reactSelectReset}
        components={animatedComponents}
        placeholder="Degree(s)"
      />
    </div>
  );
};

const SpecialtySelect = ({ formData, handleChange, specialtyOptions }: SelectProps) => {
  const handleSpecialtyChange = (selected: Option[] | null) => {
    handleChange('specialties', selected || []);
  };

  return (
    <div className="tw:space-y-2">
      <Select
        isMulti
        name="specialties"
        options={specialtyOptions}
        value={formData.specialties}
        onChange={handleSpecialtyChange}
        getOptionLabel={(option: Option) => option.label}
        getOptionValue={(option: Option) => option.value}
        className="tw:w-full"
        styles={reactSelectReset}
        components={animatedComponents}
        placeholder="Specialties"
      />
    </div>
  );
};

const LifeSupportCertificationOption = ({ value, selected, onChange }) => {
  return (
    <label
      className={`tw:!flex tw:items-center tw:justify-between tw:cursor-pointer tw:p-3 tw:border tw:rounded-md tw:transition-colors tw:duration-100
        ${
          selected
            ? 'tw:border-brand-green-1 tw:bg-brand-green-1/10 tw:text-brand-green-1 tw:dark:border-none tw:dark:text-zinc-300 tw:dark:bg-brand-green-1'
            : 'tw:border-gray-300 tw:bg-white tw:hover:border-gray-400 tw:dark:border-zinc-300 tw:dark:bg-zinc-500 tw:dark:text-zinc-300 tw:dark:border-none'
        }`}
      aria-label={`Select ${value} certification`}
    >
      <div>
        <div className="tw:font-medium">{value}</div>
      </div>
      <input
        type="checkbox"
        name="lifeSupportCertifications"
        value={value}
        checked={selected}
        onChange={onChange}
        className="tw:flex-shrink-0 tw:w-5 tw:h-5 tw:ml-4 tw:appearance-none tw:rounded tw:border-2 tw:border-gray-300 tw:checked:border-brand-green-1 tw:checked:bg-white tw:relative tw:before:content-[''] tw:before:block tw:before:w-3 tw:before:h-3 tw:before:absolute tw:before:top-1/2 tw:before:left-1/2 tw:before:-translate-x-1/2 tw:before:-translate-y-1/2 tw:checked:before:bg-brand-green-1 tw:transition-all tw:dark:checked:bg-brand-green-1 tw:dark:checked:border-white tw:dark:checked:before:bg-white"
      />
    </label>
  );
};

const LifeSupportCertificationList = ({
  formData,
  handleChange,
  lifeSupportCertificationOptions,
}) => {
  return (
    <div className="tw:space-y-2">
      <div className="tw:grid tw:grid-cols-2 tw:gap-4">
        {lifeSupportCertificationOptions.map((cert) => (
          <LifeSupportCertificationOption
            key={cert.value}
            value={cert.value}
            selected={formData.lifeSupportCertifications?.some((c) => c.value === cert.value)}
            onChange={(e) => {
              const selectedValue = e.target.value;
              const cert = lifeSupportCertificationOptions.find((c) => c.value === selectedValue);

              if (!cert) return;

              const newSelection = formData.lifeSupportCertifications.some(
                (c) => c.value === cert.value
              )
                ? formData.lifeSupportCertifications.filter((c) => c.value !== cert.value)
                : [...formData.lifeSupportCertifications, cert];

              handleChange('lifeSupportCertifications', newSelection);
            }}
          />
        ))}
      </div>
    </div>
  );
};

interface ProfessionalDetailsProps {
  stepInfo: Step;
  initialData?: ProfessionalDetailsFormData;
  registrationSteps?: string[];
  licenseStateOptions: Option[];
  boardCertificationOptions: Option[];
  degreeOptions: Option[];
  specialtyOptions: Option[];
  lifeSupportCertificationOptions: Option[];
}

export interface ProfessionalDetailsRef {
  formData: ProfessionalDetailsFormData;
}

export const ProfessionalDetails = forwardRef<ProfessionalDetailsRef, ProfessionalDetailsProps>(
  (
    {
      stepInfo,
      initialData,
      registrationSteps,
      licenseStateOptions,
      boardCertificationOptions,
      degreeOptions,
      specialtyOptions,
      lifeSupportCertificationOptions,
    },
    ref
  ) => {
    const nppesData = useMemo(() => {
      if (!initialData?.taxonomies) return null;

      const taxonomies = initialData.taxonomies;
      const uniqueSpecialties = new Set(
        taxonomies.filter((tax) => tax.specialty).map((tax) => tax.specialty)
      );

      return {
        licenses: taxonomies
          .filter((tax) => tax.state && tax.license)
          .map((tax) => ({
            value: tax.state,
            label: licenseStateOptions.find((opt) => opt.value === tax.state)?.code || tax.state,
          })),
        specialties: Array.from(uniqueSpecialties).map((specialty) => ({
          value: specialty,
          label: specialtyOptions.find((opt) => opt.value === specialty)?.label || specialty,
        })),
      };
    }, [initialData?.taxonomies, licenseStateOptions, specialtyOptions]);

    const [formData, setFormData] = useState<ProfessionalDetailsFormData>(() => {
      const baseFormData = initialData || {
        licensedStates: [],
        isResident: false,
        estimatedGraduationDate: null,
        hasCompactLicense: false,
        degree: [],
        specialties: [],
        boardCertifications: [],
        lifeSupportCertifications: [],
      };

      // Only pre-populate from NPPES if user hasn't been to this step before
      if (nppesData && !registrationSteps?.includes('PROFESSIONAL_TRAITS')) {
        if (!baseFormData.licensedStates?.length) {
          baseFormData.licensedStates = nppesData.licenses;
        }
        if (!baseFormData.specialties?.length) {
          baseFormData.specialties = nppesData.specialties;
        }
      }
      return baseFormData;
    });
    const [validationMessages, setValidationMessages] = useState<
      Record<string, { message: string; icon: React.ReactNode; colorClass: string }>
    >({});

    // Expose formData to parent
    useImperativeHandle(ref, () => ({
      formData,
    }));

    // Keep validation logic
    useEffect(() => {
      if (!registrationSteps?.includes('PROFESSIONAL_TRAITS')) return;

      const professionalDetailsValidationWarnings = {
        message: 'Missing info',
        icon: <ExclamationTriangleIcon className="tw:h-5 tw:w-5" />,
        // TODO: our normal warning color did not seem to call enough attention to fields that needed to be completed
        colorClass: 'tw:text-stoplight-danger',
      };

      const newValidationMessages: Record<
        string,
        { message: string; icon: React.ReactNode; colorClass: string }
      > = {};
      if (!formData.licensedStates?.length) {
        newValidationMessages.licensedStates = {
          ...professionalDetailsValidationWarnings,
        };
      }

      if (formData.isResident && !formData.estimatedGraduationDate) {
        newValidationMessages.estimatedGraduationDate = {
          ...professionalDetailsValidationWarnings,
        };
      }

      if (!formData.degree?.length) {
        newValidationMessages.degree = {
          ...professionalDetailsValidationWarnings,
        };
      }

      if (!formData.specialties?.length) {
        newValidationMessages.specialties = {
          ...professionalDetailsValidationWarnings,
        };
      }

      if (!formData.boardCertifications?.length) {
        newValidationMessages.boardCertifications = {
          ...professionalDetailsValidationWarnings,
        };
      }

      setValidationMessages(newValidationMessages);
    }, [formData, registrationSteps]);

    const hasStepWarnings = onboardingStepHasVisibleWarnings(validationMessages);
    const professionalFormValid =
      (formData.licensedStates?.length ?? 0) > 0 &&
      (!formData.isResident || Boolean(formData.estimatedGraduationDate)) &&
      (formData.degree?.length ?? 0) > 0 &&
      (formData.specialties?.length ?? 0) > 0 &&
      (formData.boardCertifications?.length ?? 0) > 0;
    const showStepComplete =
      Boolean(registrationSteps?.includes('PROFESSIONAL_TRAITS')) &&
      !hasStepWarnings &&
      professionalFormValid;

    const handleChange = (field: string, value: any) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

    return (
      <div className="tw:flex tw:flex-col tw:p-4 tw:lg:px-0">
        <div className="tw:border-b tw:border-zinc-200 tw:mb-4">
          <div className="tw:flex tw:flex-col tw:gap-3 tw:sm:flex-row tw:sm:items-start tw:sm:justify-between">
            <div className="tw:min-w-0 tw:flex-1">
              <Heading className="tw:!mb-0 tw:dark:!text-zinc-300">{stepInfo.label}</Heading>
              <Text className="tw:pb-2 tw:!mb-0">{stepInfo.description}</Text>
            </div>
            <StepHeaderStatusBanner hasWarnings={hasStepWarnings} showComplete={showStepComplete} />
          </div>
        </div>
        <form id="professionalDetailsForm">
          {/* License Information Group */}
          <Fieldset className="tw:!mb-6">
            <BrandLegend>Active Medical Licenses</BrandLegend>
            <FieldGroup>
              <div className="tw:mb-4">
                <StateLicenseSelect
                  formData={formData}
                  handleChange={handleChange}
                  stateOptions={licenseStateOptions}
                  validationMessages={{
                    licensedStates: validationMessages.licensedStates,
                    estimatedGraduationDate: validationMessages.estimatedGraduationDate,
                  }}
                />
              </div>
              {!formData.isResident && (
                <label
                  className="tw:border tw:border-zinc-950/10 tw:bg-white tw:rounded-md tw:p-2 tw:!grid tw:grid-cols-5 tw:gap-4 tw:hover:cursor-pointer tw:relative tw:z-0 tw:shadow-[0_1px_2px_rgba(0,0,0,0.1)] tw:dark:border-white/10 tw:dark:bg-white/5 tw:dark:shadow-none"
                  htmlFor="hasCompactLicense"
                  aria-label="Toggle compact license status"
                >
                  <div className="tw:col-span-4">
                    <div className="tw:inline-block tw:pl-[0.15rem] tw:dark:text-white">
                      Do you have a compact license?
                    </div>
                    <p className="tw:text-xs tw:text-zinc-500 tw:font-light tw:sm:text-sm/6 tw:dark:text-zinc-400">
                      Turning this on means you have an Interstate Medical Licensure Compact (IMLC)
                      License
                    </p>
                  </div>
                  <div className="tw:grid tw:content-center">
                    <input
                      className="tw:relative tw:z-0 tw:mr-2 tw:mt-[0.3rem] tw:h-3.5 tw:w-8 tw:appearance-none tw:rounded-[0.4375rem] tw:bg-neutral-300 tw:before:pointer-events-none tw:before:absolute tw:before:h-3.5 tw:before:w-3.5 tw:before:rounded-full tw:before:bg-transparent tw:before:content-[''] tw:after:absolute tw:after:z-[2] tw:after:-mt-[0.1875rem] tw:after:h-5 tw:after:w-5 tw:after:rounded-full tw:after:border-none tw:after:bg-neutral-100 tw:after:shadow-[0_0px_3px_0_rgb(0_0_0_/_7%),_0_2px_2px_0_rgb(0_0_0_/_4%)] tw:after:transition-[background-color_0.2s,transform_0.2s] tw:after:content-[''] tw:checked:bg-brand-green-1 tw:checked:after:absolute tw:checked:after:z-[2] tw:checked:after:-mt-[3px] tw:checked:after:ml-[1.0625rem] tw:checked:after:h-5 tw:checked:after:w-5 tw:checked:after:rounded-full tw:checked:after:border-none tw:checked:after:bg-brand-green-1 tw:checked:after:shadow-[0_3px_1px_-2px_rgba(0,0,0,0.2),_0_2px_2px_0_rgba(0,0,0,0.14),_0_1px_5px_0_rgba(0,0,0,0.12)] tw:checked:after:transition-[box-shadow_0.2s,transform_0.2s] tw:checked:after:content-[''] tw:hover:cursor-pointer tw:focus:outline-none tw:focus:ring-0 tw:focus:before:scale-100 tw:focus:before:opacity-[0.12] tw:focus:before:shadow-[3px_-1px_0px_13px_rgba(0,0,0,0.6)] tw:focus:before:transition-[box-shadow_0.2s,transform_0.2s] tw:focus:after:absolute tw:focus:after:z-[1] tw:focus:after:block tw:focus:after:h-5 tw:focus:after:w-5 tw:focus:after:rounded-full tw:focus:after:content-[''] tw:checked:focus:border-primary tw:checked:focus:bg-brand-green-1 tw:checked:focus:before:ml-[1.0625rem] tw:checked:focus:beforescale-100 tw:checked:focus:before:shadow-[3px_-1px_0px_13px_#3b71ca] tw:checked:focus:before:transition-[box-shadow_0.2s,transform_0.2s] tw:dark:bg-zinc-600 tw:dark:after:bg-zinc-400 tw:dark:checked:bg-brand-green-1 tw:dark:checked:after:bg-brand-green-1"
                      type="checkbox"
                      id="hasCompactLicense"
                      name="hasCompactLicense"
                      role="switch"
                      checked={formData.hasCompactLicense}
                      onChange={(e) => {
                        handleChange('hasCompactLicense', e.target.checked);
                      }}
                    />
                  </div>
                </label>
              )}
            </FieldGroup>
          </Fieldset>

          {/* Qualifications Group */}
          <Fieldset className="tw:mb-6">
            <BrandLegend>Credentials</BrandLegend>
            <FieldGroup>
              <div className="tw:mb-4">
                <DegreeSelect
                  formData={formData}
                  handleChange={handleChange}
                  degreeOptions={degreeOptions}
                  specialtyOptions={specialtyOptions}
                />
                <ValidationMessage
                  message={validationMessages.degree?.message}
                  icon={validationMessages.degree?.icon}
                  colorClass={validationMessages.degree?.colorClass}
                />
              </div>
              <div className="tw:mb-4">
                <SpecialtySelect
                  formData={formData}
                  handleChange={handleChange}
                  degreeOptions={degreeOptions}
                  specialtyOptions={specialtyOptions}
                />
                <ValidationMessage
                  message={validationMessages.specialties?.message}
                  icon={validationMessages.specialties?.icon}
                  colorClass={validationMessages.specialties?.colorClass}
                />
              </div>
            </FieldGroup>
          </Fieldset>

          {/* Board Certifications Group */}
          <Fieldset className="tw:!mb-6">
            <BrandLegend>Board Certifications</BrandLegend>
            <FieldGroup className="tw:!space-y-2">
              <BoardCertificationList
                formData={formData}
                handleChange={handleChange}
                boardCertificationOptions={boardCertificationOptions}
              />
              <ValidationMessage
                message={validationMessages.boardCertifications?.message}
                icon={validationMessages.boardCertifications?.icon}
                colorClass={validationMessages.boardCertifications?.colorClass}
              />
            </FieldGroup>
          </Fieldset>

          {/* Additional Qualifications Group */}
          <Fieldset className="tw:mb-6">
            <BrandLegend>Active Life Support Certifications</BrandLegend>
            {/* <ValidationMessage message={validationMessages.certifications} /> */}
            <FieldGroup>
              <LifeSupportCertificationList
                formData={formData}
                handleChange={handleChange}
                lifeSupportCertificationOptions={lifeSupportCertificationOptions}
              />
            </FieldGroup>
          </Fieldset>

          <div className="tw:flex tw:justify-end">
            <button type="submit" className="tw:hidden">
              I Agree
            </button>
          </div>
        </form>
      </div>
    );
  }
);
ProfessionalDetails.displayName = 'ProfessionalDetails';
